import { useApiClient } from '@yuksalish/api-client'
import axios from 'axios'
import type { AxiosInstance } from 'axios'
import type {
  Assignment,
  BasketListFilters,
  BasketListItem,
  BasketResult,
  ColorReference,
  CompletionRequest,
  CompletionResult,
  CreateBasketRequest,
  CreateDailyKnittingOutputBatchRequest,
  DailyKnittingOutput,
  DailyKnittingOutputListParams,
  DailyKnittingOutputSummaryParams,
  DailyKnittingOutputSummaryRow,
  DashboardDto,
  DowntimeEvent,
  DowntimeReason,
  EmployeeOption,
  EquipmentItem,
  HandoverRecord,
  LotDetail,
  MachineStatus,
  PageMeta,
  ProductOption,
  ReopenReasonCode,
  ShiftDashboardData,
  ShopfloorDailyOutput,
  ShopfloorLot,
  ShopfloorOrder,
  ShopfloorOrderPage,
  ShopfloorStage,
  ShopfloorStageEntry,
  WarehouseAgentOption,
  WorkerCardLogin,
  WorkQueueItem,
  WorkQueueStatus,
} from '../types/shopfloor'

export interface ApiList<T> {
  data: T[]
  meta?: {
    page?: number
    current_page?: number
    per_page?: number
    total?: number
    total_pages?: number
    last_page?: number
  }
}

export interface ApiItem<T> {
  data: T
}

export interface PagedResult<T> {
  items: T[]
  meta: PageMeta
}

function completionPercent(completed: number, planned: number): number {
  if (!planned) return 0
  return Math.round((completed / planned) * 100)
}

/** Client-generated idempotency key for safe retries of mutating calls. */
function newIdempotencyKey(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `${prefix}:${random}`
}

function parsePageMeta(meta: ApiList<never>['meta'], fallbackPage = 1, fallbackPerPage = 20): PageMeta {
  return {
    page: meta?.page ?? meta?.current_page ?? fallbackPage,
    per_page: meta?.per_page ?? fallbackPerPage,
    total: meta?.total ?? 0,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}

export function useShopfloorApi() {
  const client = useApiClient()

  /**
   * Client used for kiosk device calls. It reuses the configured API base URL
   * but has no session interceptor: the caller supplies the device token and a
   * rejected device token must never clear the operator's session.
   */
  let deviceClient: AxiosInstance | null = null
  function kioskDeviceClient(): AxiosInstance {
    deviceClient ??= axios.create({
      baseURL: client.defaults.baseURL,
      headers: { Accept: 'application/json' },
    })
    return deviceClient
  }

  async function listActiveOrders(filters: {
    search?: string
    status?: string
    page: number
    per_page: number
  }): Promise<ShopfloorOrderPage> {
    const response = await client.get<ApiList<ShopfloorOrder>>('/production-orders', {
      params: {
        search: filters.search || undefined,
        status: filters.status || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    const meta = response.data.meta
    return {
      items: response.data.data,
      page: meta?.page ?? meta?.current_page ?? filters.page,
      per_page: meta?.per_page ?? filters.per_page,
      total: meta?.total ?? response.data.data.length,
      total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
    }
  }

  /** Orders available for shopfloor input: newly planned and already running. */
  async function listCurrentProductionOrders(): Promise<ShopfloorOrder[]> {
    const statuses = ['planned', 'in_progress'] as const
    const collected: ShopfloorOrder[] = []

    for (const status of statuses) {
      let page = 1
      let totalPages = 1
      do {
        const result = await listActiveOrders({ status, page, per_page: 100 })
        collected.push(...result.items)
        totalPages = Math.max(1, result.total_pages)
        page += 1
      } while (page <= totalPages)
    }

    return Array.from(new Map(collected.map((order) => [order.id, order])).values())
      .sort((left, right) => {
        const dateDifference = String(right.created_at ?? '').localeCompare(String(left.created_at ?? ''))
        return dateDifference || right.id - left.id
      })
  }

  async function getOrder(orderId: number): Promise<ShopfloorOrder> {
    const response = await client.get<ApiItem<ShopfloorOrder>>(`/production-orders/${orderId}`)
    return response.data.data
  }

  async function listStages(orderId: number): Promise<ShopfloorStage[]> {
    const response = await client.get<ApiList<ShopfloorStage>>(`/production-orders/${orderId}/stages`)
    return response.data.data
  }

  async function listLots(orderId: number): Promise<ShopfloorLot[]> {
    const response = await client.get<ApiList<ShopfloorLot>>(`/production-orders/${orderId}/lots`)
    return response.data.data
  }

  /** Order-level stage close (all lots). Returns the refreshed stage board. */
  async function closeStage(
    orderId: number,
    stageCode: string,
    payload: { employee_id?: number | null; notes?: string },
  ): Promise<ShopfloorStage[]> {
    const response = await client.post<ApiList<ShopfloorStage>>(
      `/production-orders/${orderId}/stages/${encodeURIComponent(stageCode)}/close`,
      { ...payload, idempotency_key: newIdempotencyKey('stage-close') },
    )
    return response.data.data
  }

  /** Controlled stage reopen (supervisor-gated). Returns the refreshed stage board. */
  async function reopenStage(
    orderId: number,
    stageCode: string,
    payload: { reason_code: ReopenReasonCode; reason_text: string; employee_id?: number | null; notes?: string },
  ): Promise<ShopfloorStage[]> {
    const response = await client.post<ApiList<ShopfloorStage>>(
      `/production-orders/${orderId}/stages/${encodeURIComponent(stageCode)}/reopen`,
      { ...payload, idempotency_key: newIdempotencyKey('stage-reopen') },
    )
    return response.data.data
  }

  /** Stage-level audit trail (progress / close / reopen entries). */
  async function getStageEntries(
    orderId: number,
    stageCode: string,
    limit = 100,
  ): Promise<ShopfloorStageEntry[]> {
    const response = await client.get<ApiList<ShopfloorStageEntry>>(
      `/production-orders/${orderId}/stages/${encodeURIComponent(stageCode)}/entries`,
      { params: { limit } },
    )
    return response.data.data
  }

  /** Full lot detail: lot header + per-lot stage tracking + audit entries. */
  async function getLotDetail(orderId: number, lotNumber: string): Promise<LotDetail> {
    const response = await client.get<ApiItem<LotDetail>>(
      `/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}`,
    )
    return response.data.data
  }

  /** Production employee catalog for assignment and progress pickers. */
  async function listEmployees(): Promise<EmployeeOption[]> {
    const response = await client.get<ApiList<EmployeeOption>>('/employees', {
      params: { employee_type: 'production', is_active: 1, page: 1, per_page: 200 },
    })
    return response.data.data
  }

  async function listProducts(search = ''): Promise<ProductOption[]> {
    const response = await client.get<ApiList<ProductOption>>('/products', {
      params: { search: search || undefined, is_active: 1, page: 1, per_page: 100 },
    })
    return response.data.data
  }

  async function listColorReferences(filters: {
    search?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<ColorReference>> {
    const response = await client.get<ApiList<ColorReference>>('/reference-items/color', {
      params: {
        search: filters.search || undefined,
        is_active: 1,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function listSemiFinishedWarehouses(): Promise<WarehouseAgentOption[]> {
    const response = await client.get<ApiList<WarehouseAgentOption>>('/agents', {
      params: { role: 'warehouse', is_active: 1, page: 1, per_page: 200 },
    })
    return response.data.data.filter(
      (agent) =>
        (agent.roles?.includes('warehouse') ?? true) &&
        agent.warehouse_details?.inventory_item_type === 'semi_finished',
    )
  }

  async function createLot(
    orderId: number,
    payload: {
      stage_code: string
      quantity: number
      color_summary?: Record<string, number> | Array<{ color: string; quantity: number }>
      notes?: string
    },
  ): Promise<ShopfloorLot> {
    const response = await client.post<ApiItem<ShopfloorLot>>(`/production-orders/${orderId}/lots`, payload)
    return response.data.data
  }

  async function recordLotProgress(
    orderId: number,
    lotNumber: string,
    stageCode: string,
    payload: {
      completed_delta: number
      rejected_delta: number
      rework_delta: number
      employee_id: number
      notes?: string
    },
  ): Promise<LotDetail> {
    const response = await client.post<ApiItem<LotDetail>>(
      `/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}/stages/${encodeURIComponent(stageCode)}/progress`,
      { ...payload, idempotency_key: newIdempotencyKey('lot-progress') },
    )
    return response.data.data
  }

  async function closeLotStage(
    orderId: number,
    lotNumber: string,
    stageCode: string,
    payload: { employee_id?: number; notes?: string },
  ): Promise<LotDetail> {
    const response = await client.post<ApiItem<LotDetail>>(
      `/production-orders/${orderId}/lots/${encodeURIComponent(lotNumber)}/stages/${encodeURIComponent(stageCode)}/close`,
      { ...payload, idempotency_key: newIdempotencyKey('lot-close') },
    )
    return response.data.data
  }

  async function fetchDailyOutput(date: string): Promise<ShopfloorDailyOutput> {
    const response = await client.get<ApiItem<ShopfloorDailyOutput>>('/production-execution/daily-output', {
      params: { date },
    })
    return response.data.data
  }

  async function createKnittingOutputBatch(
    payload: CreateDailyKnittingOutputBatchRequest,
  ): Promise<DailyKnittingOutput[]> {
    const response = await client.post<ApiList<DailyKnittingOutput>>(
      '/production-execution/knitting-output/batch',
      payload,
    )
    return response.data.data
  }

  async function listKnittingOutput(
    params: DailyKnittingOutputListParams,
  ): Promise<PagedResult<DailyKnittingOutput>> {
    const response = await client.get<ApiList<DailyKnittingOutput>>('/production-execution/knitting-output', {
      params,
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, params.page ?? 1, params.per_page ?? 20),
    }
  }

  /**
   * Correct a wrongly recorded knitting entry. `scope: 'batch'` removes the whole tablet
   * submission the row belongs to; `'single'` removes only that row.
   */
  async function deleteKnittingOutput(id: number, scope: 'single' | 'batch' = 'batch'): Promise<void> {
    await client.delete(`/production-execution/knitting-output/${id}`, { params: { scope } })
  }

  async function summarizeKnittingOutput(
    params: DailyKnittingOutputSummaryParams,
  ): Promise<DailyKnittingOutputSummaryRow[]> {
    const response = await client.get<ApiList<DailyKnittingOutputSummaryRow>>(
      '/production-execution/knitting-output/summary',
      { params },
    )
    return response.data.data
  }

  /** Real backend dashboard aggregate (GET /production-execution/dashboard). */
  async function fetchDashboard(): Promise<ShiftDashboardData> {
    const response = await client.get<ApiItem<DashboardDto>>('/production-execution/dashboard')
    const d = response.data.data
    return {
      activeOrders: d.active_orders,
      totalPlannedPairs: d.total_planned_pairs,
      totalProducedPairs: d.total_produced_pairs,
      completionRate: d.completion_rate,
      bottlenecks: d.bottlenecks.map((b) => ({
        stage: b.stage_name,
        orderId: b.order_id,
        orderCode: b.order_code ?? '',
        stageCode: b.stage_code,
        completion: b.completion,
        queuedLots: 0,
      })),
      assignedEmployees: d.assigned_employees,
      totalEmployees: d.total_employees,
      runningMachines: d.running_machines,
      idleMachines: d.idle_machines,
      rejectedPairs: d.rejected_pairs,
      reworkPairs: d.rework_pairs,
    }
  }

  async function listDowntimeReasons(): Promise<DowntimeReason[]> {
    const response = await client.get<ApiList<DowntimeReason>>('/production-execution/downtime-reasons')
    return response.data.data
  }

  async function listDowntime(filters: {
    shift?: string
    machine_id?: number
    reason_code?: string
    open_only?: boolean
    date?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<DowntimeEvent>> {
    const response = await client.get<ApiList<DowntimeEvent>>('/production-execution/downtime', {
      params: {
        shift: filters.shift || undefined,
        machine_id: filters.machine_id,
        reason_code: filters.reason_code || undefined,
        open_only: filters.open_only ? '1' : undefined,
        date: filters.date || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function openDowntime(payload: {
    reason_code: string
    machine_id?: number | null
    order_id?: number | null
    lot_number?: string | null
    start_time: string
    notes?: string
  }): Promise<DowntimeEvent> {
    const response = await client.post<ApiItem<DowntimeEvent>>('/production-execution/downtime', payload)
    return response.data.data
  }

  async function closeDowntime(id: number, payload: { end_time: string; notes?: string }): Promise<DowntimeEvent> {
    const response = await client.post<ApiItem<DowntimeEvent>>(`/production-execution/downtime/${id}/close`, payload)
    return response.data.data
  }

  async function listHandovers(filters: {
    shift?: string
    date?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<HandoverRecord>> {
    const response = await client.get<ApiList<HandoverRecord>>('/production-execution/handovers', {
      params: {
        shift: filters.shift || undefined,
        date: filters.date || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function createHandover(payload: {
    shift: string
    from: string
    to: string
    orders_handed_over: number
    issues_flagged: number
    attention_required: boolean
    notes?: string
  }): Promise<HandoverRecord> {
    const response = await client.post<ApiItem<HandoverRecord>>('/production-execution/handovers', payload)
    return response.data.data
  }

  async function listAssignments(filters: {
    shift?: string
    type?: 'employee' | 'machine'
    order_id?: number
    employee_id?: number
    machine_id?: number
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<Assignment>> {
    const response = await client.get<ApiList<Assignment>>('/production-execution/assignments', {
      params: {
        shift: filters.shift || undefined,
        type: filters.type || undefined,
        order_id: filters.order_id,
        employee_id: filters.employee_id,
        machine_id: filters.machine_id,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function createAssignment(payload: {
    type: 'employee' | 'machine'
    employee_id?: number | null
    machine_id?: number | null
    order_id: number
    lot_number?: string | null
    stage_code: string
    shift: string
    assigned_at: string
    notes?: string
  }): Promise<Assignment> {
    const response = await client.post<ApiItem<Assignment>>('/production-execution/assignments', payload)
    return response.data.data
  }

  async function endAssignment(id: number): Promise<Assignment> {
    const response = await client.delete<ApiItem<Assignment>>(`/production-execution/assignments/${id}`)
    return response.data.data
  }

  async function listMachines(filters: {
    status?: 'running' | 'idle' | 'down' | 'setup'
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<MachineStatus>> {
    const response = await client.get<ApiList<MachineStatus>>('/production-execution/machines', {
      params: {
        status: filters.status || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  /** Equipment catalog (reference items) used by machine pickers. */
  async function listEquipment(): Promise<EquipmentItem[]> {    const response = await client.get<ApiList<EquipmentItem>>('/reference-items/equipment', {
      params: { is_active: 1 },
    })
    return response.data.data
  }

  // --- Phase 5 work queue + atomic completion -----------------------------

  /**
   * Identify the worker at a kiosk terminal by scanned card.
   *
   * `/kiosk/worker-login` is authenticated by the KIOSK DEVICE token, not by the
   * signed-in user session, so the call goes through a dedicated client that
   * sends the device token as its bearer credential. Sharing the desktop client
   * would send the operator's session token (rejected as "Invalid kiosk token")
   * and would also sign the operator out on a 401.
   */
  async function workerCardLogin(
    payload: { card_uid: string },
    kioskToken: string,
  ): Promise<WorkerCardLogin> {
    const response = await kioskDeviceClient().post<ApiItem<WorkerCardLogin>>(
      '/kiosk/worker-login',
      payload,
      { headers: { Authorization: `Bearer ${kioskToken}` } },
    )
    return response.data.data
  }

  /** Eligible work items for one workshop/stage (`stage_code` is required). */
  async function listWorkQueue(filters: {
    stage_code: string
    production_order_id?: number
    department_agent_id?: number
    warehouse_agent_id?: number
    status?: WorkQueueStatus
    search?: string
    page?: number
    per_page?: number
  }): Promise<PagedResult<WorkQueueItem>> {
    const response = await client.get<ApiList<WorkQueueItem>>('/production-execution/work-queue', {
      params: {
        stage_code: filters.stage_code,
        production_order_id: filters.production_order_id,
        department_agent_id: filters.department_agent_id,
        warehouse_agent_id: filters.warehouse_agent_id,
        status: filters.status || undefined,
        search: filters.search || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  /**
   * Record accepted knitting output. Retry the exact same payload (including
   * `client_event_id`) for an idempotent replay.
   */
  async function submitCompletion(payload: CompletionRequest): Promise<CompletionResult> {
    const response = await client.post<ApiItem<CompletionResult>>('/production-execution/completions', payload)
    return response.data.data
  }

  /**
   * One knitting submission = one physical basket.
   *
   * The whole submission is one transaction (all colour/size lines or none), and
   * replaying the same `client_event_id` returns the original basket. Lines are
   * addressed with their `work_item_id` (`variant:<id>` at the knitting stage).
   */
  async function createProductionBasket(payload: CreateBasketRequest): Promise<BasketResult> {
    const response = await client.post<ApiItem<BasketResult>>('/production-execution/baskets', payload)
    return response.data.data
  }

  /**
   * Basket list (each row carries its colour/size lines). `date_from`/`date_to`
   * filter on `output_date`; combine them with the exact timestamp of
   * `created_at` when a precise window such as "last 24 hours" is needed.
   */
  async function listProductionBaskets(filters: BasketListFilters = {}): Promise<PagedResult<BasketListItem>> {
    const response = await client.get<ApiList<BasketListItem>>('/production-execution/baskets', {
      params: {
        employee_id: filters.employee_id,
        product_id: filters.product_id,
        production_order_id: filters.production_order_id,
        warehouse_agent_id: filters.warehouse_agent_id,
        stage_code: filters.stage_code || undefined,
        status: filters.status || undefined,
        shift: filters.shift || undefined,
        date: filters.date || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        basket_code: filters.basket_code || undefined,
        color_name: filters.color_name || undefined,
        search: filters.search || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })
    return {
      items: response.data.data,
      meta: parsePageMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  /** Basket detail (basket + lines + per-stage quantities). */
  async function getProductionBasket(basketId: number): Promise<BasketResult> {
    const response = await client.get<ApiItem<BasketResult>>(`/production-execution/baskets/${basketId}`)
    return response.data.data
  }

  /**
   * Close a basket so every member line advances to the next BOM operation in
   * one transaction. `stage_code` is optional on the backend.
   */
  async function closeProductionBasket(
    basketId: number,
    payload: { stage_code?: string; employee_id?: number | null; idempotency_key: string; notes?: string | null },
  ): Promise<BasketResult> {
    const response = await client.post<ApiItem<BasketResult>>(
      `/production-execution/baskets/${basketId}/close`,
      payload,
    )
    return response.data.data
  }

  return {
    listActiveOrders,
    listCurrentProductionOrders,
    getOrder,
    listStages,
    listLots,
    createLot,
    recordLotProgress,
    closeLotStage,
    closeStage,
    reopenStage,
    getStageEntries,
    getLotDetail,
    listEmployees,
    listProducts,
    listColorReferences,
    listSemiFinishedWarehouses,
    fetchDailyOutput,
    createKnittingOutputBatch,
    listKnittingOutput,
    deleteKnittingOutput,
    summarizeKnittingOutput,
    fetchDashboard,
    listDowntimeReasons,
    listDowntime,
    openDowntime,
    closeDowntime,
    listHandovers,
    createHandover,
    listAssignments,
    createAssignment,
    endAssignment,
    listMachines,
    listEquipment,
    listWorkQueue,
    submitCompletion,
    createProductionBasket,
    listProductionBaskets,
    getProductionBasket,
    closeProductionBasket,
    workerCardLogin,
  }
}

export type ShopfloorApi = ReturnType<typeof useShopfloorApi>
export type { AxiosInstance }
